import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { Validators } from "@angular/forms";
import type { ConfigOption } from "@ngx-formly/core";
import { NewUiFormlyCandidateListMultiEditComponent } from "./candidate-list-multi-edit/new-ui-formly-candidate-list-multi-edit.component";
import { NewUiFormlyCertifiedEducationExamsComponent } from "./certified-education-exams/new-ui-formly-certified-education-exams.component";
import { NewUiFormlyCheckboxComponent } from "./checkbox/new-ui-formly-checkbox.component";
import { NewUiFormlyChipsComponent } from "./chips-with comment/new-ui-formly-chips.component";
import { NewUiFormlyChipsInputComponent } from "./chips-input/new-ui-formly-chips-input.component";
import { FormlyClassNameExtension } from "./class-name/formly-class-name.extension";
import { NewUiFormlyCompetenciesComponent } from "./competencies/new-ui-formly-competencies.component";
import { NewUiFormlyCompetencyComponent } from "./competency/new-ui-formly-competency.component";
import { NewUiFormlyDatepickerComponent } from "./datepicker/new-ui-formly-datepicker.component";
import { NewUiFormlyDateWithTimeComponent } from "./date-with-time/new-ui-formly-date-with-time.component";
import { NewUiFormlyDividerComponent } from "./divider/new-ui-formly-divider.component";
import { NewUiFormlyDocumentDateComponent } from "./document-date/new-ui-formly-document-date.component";
import { NewUiFormlyDropdownComponent } from "./dropdown/new-ui-formly-dropdown.component";
import { NewUiFormlyEnhancedDropdownComponent } from "./enhanced-dropdown/new-ui-formly-enhanced-dropdown.component";
import { NewUiFormlyInputComponent } from "./input/new-ui-formly-input.component";
import { NewUiFormlyInputSwitchComponent } from "./inputSwitch/new-ui-formly-inputSwitch.component";
import { NewUiFormlyLanguageLevelByPlacementTestComponent } from "./language-level-by-placement-test/new-ui-formly-language-level-by-placement-test.component";
import { NewUiFormlyMenuBarComponent } from "./menu/new-ui-formly-menu-bar.component";
import { NewUiFormlyMessageComponent } from "./message/new-ui-formly-message.component";
import { NewUiFormlyMultiSelectComponent } from "./multiselect/new-ui-formly-multiselect.component";
import { NewUiFormlyPanelComponent } from "./panel/new-ui-formly-panel.component";
import { NewUiFormlyProfessionSelectorComponent } from "./profession-selector/new-ui-formly-profession-selector.component";
import { NewUiFormlyRadioComponent } from "./radio-button/new-ui-formly-radio.component";
import { FormlyReadonlyExtension } from "./readonly/formly-readonly.extension";
import { NewUiFormlyRepeatTabsComponent } from "./repeat-tabs/new-ui-formly-repeat-tabs.component";
import { NewUiFormlyRepeatComponent } from "./repeat/new-ui-formly-repeat.component";
import { NewUiFormlyRichTextComponent } from "./rich-text/new-ui-formly-rich-text.component";
import { FormlyRootExtension } from "./root/formly-root.extension";
import { NewUiFormlySelectbuttonComponent } from "./selectbutton/new-ui-formly-selectbutton.component";
import { FormlyShowValidationExtension } from "./show-validation/formly-show-validation.extension";
import { NewUiFormlyTabsComponent } from "./tabs/new-ui-formly-tabs.component";
import { NewUiFormlyTextareaComponent } from "./textarea/new-ui-formly-textarea.component";
import { FormlyTranslateExtension } from "./translate/formly-translate.extension";
import { NewUiFormlyTreeselectComponent } from "./treeselect/new-ui-formly-treeselect.component";
import { NewUiFormlyVaccinationInformationComponent } from "./vaccination-information/new-ui-formly-vaccination-information.component";

export const newUiFormlyConfig: ConfigOption = {
  extras: { immutable: true },
  validationMessages: [
    {
      name: "required",
      message: () => translateSafely("common.validation.required"),
    },
    {
      name: "minLength",
      message: (_, field) => translateSafely("common.validation.minLength", { length: field.props.minLength }),
    },
    {
      name: "maxLength",
      message: (_, field) => translateSafely("common.validation.maxLength", { length: field.props.maxLength }),
    },
    {
      name: "min",
      message: (_, field) => translateSafely("common.validation.min", { value: field.props.min }),
    },
    {
      name: "max",
      message: (_, field) => translateSafely("common.validation.max", { value: field.props.max }),
    },
    {
      name: "minDate",
      message: () => translateSafely("common.validation.minDate"),
    },
    {
      name: "maxDate",
      message: () => translateSafely("common.validation.maxDate"),
    },
    {
      name: "range",
      message: () => translateSafely("common.validation.range"),
    },
    {
      name: "pattern",
      message: () => translateSafely("common.validation.pattern"),
    },
  ],
  validators: [
    // enforces that a checkbox must be checked (value === true), unlike `required`
    // which only enforces that the value is not null/undefined (so `false` passes)
    { name: "requiredTrue", validation: (control) => Validators.requiredTrue(control) },
  ],
  types: [
    { name: "panel", component: NewUiFormlyPanelComponent },
    { name: "menubar", component: NewUiFormlyMenuBarComponent },
    { name: "tabs", component: NewUiFormlyTabsComponent },
    { name: "repeat", component: NewUiFormlyRepeatComponent },
    { name: "repeat-tabs", component: NewUiFormlyRepeatTabsComponent },
    { name: "candidate-list-multi-edit", component: NewUiFormlyCandidateListMultiEditComponent },
    { name: "competencies", component: NewUiFormlyCompetenciesComponent },
    { name: "divider", component: NewUiFormlyDividerComponent },
    { name: "input", component: NewUiFormlyInputComponent },
    { name: "textarea", component: NewUiFormlyTextareaComponent },
    { name: "rich-text", component: NewUiFormlyRichTextComponent },
    { name: "dropdown", component: NewUiFormlyDropdownComponent },
    { name: "multiselect", component: NewUiFormlyMultiSelectComponent },
    { name: "treeselect", component: NewUiFormlyTreeselectComponent },
    { name: "chips", component: NewUiFormlyChipsComponent },
    { name: "chips-input", component: NewUiFormlyChipsInputComponent },
    { name: "autocomplete", component: NewUiFormlyEnhancedDropdownComponent },
    { name: "datepicker", component: NewUiFormlyDatepickerComponent },
    { name: "date-with-time", component: NewUiFormlyDateWithTimeComponent },
    { name: "document-date", component: NewUiFormlyDocumentDateComponent },
    { name: "checkbox", component: NewUiFormlyCheckboxComponent },
    { name: "input-switch", component: NewUiFormlyInputSwitchComponent },
    { name: "selectbutton", component: NewUiFormlySelectbuttonComponent },
    { name: "radio", component: NewUiFormlyRadioComponent },
    { name: "profession", component: NewUiFormlyProfessionSelectorComponent },
    { name: "competency", component: NewUiFormlyCompetencyComponent },
    { name: "language-level-by-placement-test", component: NewUiFormlyLanguageLevelByPlacementTestComponent },
    { name: "certified-education-exams", component: NewUiFormlyCertifiedEducationExamsComponent },
    { name: "vaccination-information", component: NewUiFormlyVaccinationInformationComponent },
    { name: "message", component: NewUiFormlyMessageComponent },
  ],
  extensions: [
    { name: "root", extension: new FormlyRootExtension(), priority: 10 },
    { name: "readonly", extension: new FormlyReadonlyExtension(), priority: 20 },
    { name: "className", extension: new FormlyClassNameExtension(), priority: 30 },
    { name: "translate", extension: new FormlyTranslateExtension(), priority: 40 },
    { name: "showValidation", extension: new FormlyShowValidationExtension(), priority: 50 },
  ],
};
